import { useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format, startOfWeek, endOfWeek, getWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface WeekCellProps {
  value: string | number | null;
  onChange: (value: string | null) => void;
  readOnly?: boolean;
}

export function WeekCell({ value, onChange, readOnly }: WeekCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  let selectedDate: Date | undefined;
  if (value) {
    const tempDate = typeof value === 'number' ? new Date(value) : new Date(value);
    if (!isNaN(tempDate.getTime())) {
      selectedDate = tempDate;
    }
  }

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      onChange(date.toISOString());
      setIsOpen(false);
    }
  };

  if (!selectedDate) {
    if (readOnly) {
      return <span className="text-gray-400 text-sm">-</span>;
    }
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer hover:text-gray-600">
            <CalendarIcon className="w-4 h-4" />
            <span>Selecionar semana</span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            locale={ptBR}
          />
        </PopoverContent>
      </Popover>
    );
  }

  const weekNumber = getWeek(selectedDate, { locale: ptBR });
  const weekStart = startOfWeek(selectedDate, { locale: ptBR });
  const weekEnd = endOfWeek(selectedDate, { locale: ptBR });
  
  const weekLabel = `Semana ${weekNumber}, ${format(selectedDate, 'yyyy')}`;
  const dateRange = `${format(weekStart, 'dd/MM')} - ${format(weekEnd, 'dd/MM')}`;

  if (readOnly) {
    return (
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-700">{weekLabel}</span>
        <span className="text-xs text-gray-500">{dateRange}</span>
      </div>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
          <CalendarIcon className="w-4 h-4 text-blue-500" />
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-700">{weekLabel}</span>
            <span className="text-xs text-gray-500">{dateRange}</span>
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          locale={ptBR}
        />
      </PopoverContent>
    </Popover>
  );
}
